// hms.id.fe.provider/src/_metronic/store/notificationStore.ts
import {create} from 'zustand'
import {
  ChatNotification,
  StatusNotification,
  PusherChatMessagePayload,
  PusherStatusUpdatePayload,
} from '../../types/notificationTypes'

/**
 * Legacy notification interface for backward compatibility
 */
interface LegacyNotification {
  ClaimNumber: string
  Status: string
  UpdatedBy: string
  UpdatedAt: string
  isRead: boolean
}

/**
 * Unified notification store interface
 * Supports both chat and status notifications
 */
interface NotificationStore {
  // Legacy notifications (backward compatibility)
  notifications: LegacyNotification[]
  addNotification: (notification: LegacyNotification) => void
  clearNotifications: () => void
  markAsRead: (index: number) => void

  // Chat notifications
  chatNotifications: ChatNotification[]
  chatUnreadCount: number
  setChatNotifications: (notifications: ChatNotification[]) => void
  addChatNotification: (notification: ChatNotification) => void
  addChatNotificationFromPusher: (message: PusherChatMessagePayload) => void
  clearChatNotifications: () => void
  markChatAsRead: (claimNumber: string) => void

  // Status notifications
  statusNotifications: StatusNotification[]
  statusUnreadCount: number
  setStatusNotifications: (notifications: StatusNotification[]) => void
  addStatusNotification: (notification: StatusNotification) => void
  addStatusNotificationFromPusher: (message: PusherStatusUpdatePayload) => void
  clearStatusNotifications: () => void
  markStatusAsRead: (claimNumber: string) => void

  // Total unread count
  getTotalUnreadCount: () => number
}

const useNotificationStore = create<NotificationStore>((set, get) => ({
  // Legacy notifications
  notifications: [],
  addNotification: (notification) =>
    set((state) => ({
      notifications: [...state.notifications, notification],
    })),
  clearNotifications: () => set({notifications: []}),
  markAsRead: (index) =>
    set((state) => {
      const notifications = [...state.notifications]
      if (notifications[index]) {
        notifications[index].isRead = true
      }
      return {notifications}
    }),

  // Chat notifications
  chatNotifications: [],
  chatUnreadCount: 0,
  setChatNotifications: (notifications) =>
    set({
      chatNotifications: notifications,
      chatUnreadCount: notifications.filter((n) => !n.isOpen).length,
    }),
  addChatNotification: (notification) =>
    set((state) => ({
      chatNotifications: [notification, ...state.chatNotifications],
      chatUnreadCount: notification.isOpen ? state.chatUnreadCount : state.chatUnreadCount + 1,
    })),
  addChatNotificationFromPusher: (message) =>
    set((state) => {
      const newNotification: ChatNotification = {
        claimNumber: message.claimNumber,
        remark: message.remark,
        createdAt: message.createdAt,
        insurerName: message.insurerName || '',
        customerName: message.customerName || '',
        providerName: '',
        isOpen: message.chatIsOpen,
        latestRole: message.role,
      }
      return {
        chatNotifications: [newNotification, ...state.chatNotifications],
        chatUnreadCount: message.chatIsOpen ? state.chatUnreadCount : state.chatUnreadCount + 1,
      }
    }),
  clearChatNotifications: () => set({chatNotifications: [], chatUnreadCount: 0}),
  markChatAsRead: (claimNumber) =>
    set((state) => {
      const chatNotifications = state.chatNotifications.map((n) =>
        n.claimNumber === claimNumber ? {...n, isOpen: true} : n
      )
      return {
        chatNotifications,
        chatUnreadCount: chatNotifications.filter((n) => !n.isOpen).length,
      }
    }),

  // Status notifications
  statusNotifications: [],
  statusUnreadCount: 0,
  setStatusNotifications: (notifications) =>
    set({
      statusNotifications: notifications,
      statusUnreadCount: notifications.filter((n) => !n.isOpen).length,
    }),
  addStatusNotification: (notification) =>
    set((state) => ({
      statusNotifications: [notification, ...state.statusNotifications],
      statusUnreadCount: notification.isOpen
        ? state.statusUnreadCount
        : state.statusUnreadCount + 1,
    })),
  addStatusNotificationFromPusher: (message) =>
    set((state) => {
      const newNotification: StatusNotification = {
        claimNumber: message.ClaimNumber,
        status: message.StatusClaim,
        remark: `Claim status changed to ${message.StatusClaim}`,
        createdAt: new Date().toISOString(),
        providerName: '',
        isOpen: false,
      }
      return {
        statusNotifications: [newNotification, ...state.statusNotifications],
        statusUnreadCount: state.statusUnreadCount + 1,
      }
    }),
  clearStatusNotifications: () => set({statusNotifications: [], statusUnreadCount: 0}),
  markStatusAsRead: (claimNumber) =>
    set((state) => {
      const statusNotifications = state.statusNotifications.map((n) =>
        n.claimNumber === claimNumber ? {...n, isOpen: true} : n
      )
      return {
        statusNotifications,
        statusUnreadCount: statusNotifications.filter((n) => !n.isOpen).length,
      }
    }),

  // Total unread count
  getTotalUnreadCount: () => {
    const state = get()
    return state.chatUnreadCount + state.statusUnreadCount
  },
}))

export default useNotificationStore
